import { ChevronDown, ChevronRight, Pencil, Star } from 'lucide-react'
import { Fragment, useMemo, useState } from 'react'
import { toast } from 'sonner'

import { createTemplateFromMealItems } from '@/api/services/nutrition'
import type { FoodTemplate, MealItem, MealLog } from '@/api/types/nutrition'
import { Button } from '@/components/ui/button'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'

import { MealSheet } from './meal-sheet'
import { isMealType, MEAL_TYPE_ICONS, MEAL_TYPE_LABELS, MEAL_TYPE_ORDER } from './meal-types'
import { scaleQuantity, singleItemTemplateNames } from './utils'

type GroupTotals = {
  meal_type: string
  meals: MealLog[]
  total_calories: number
  total_protein_g: number
  total_fat_g: number
  total_carb_g: number
}

function groupMealsByType(meals: MealLog[]): GroupTotals[] {
  const map = new Map<string, GroupTotals>()
  for (const meal of meals) {
    let group = map.get(meal.meal_type)
    if (!group) {
      group = {
        meal_type: meal.meal_type,
        meals: [],
        total_calories: 0,
        total_protein_g: 0,
        total_fat_g: 0,
        total_carb_g: 0,
      }
      map.set(meal.meal_type, group)
    }
    group.meals.push(meal)
    group.total_calories += meal.total_calories ?? 0
    group.total_protein_g += meal.total_protein_g ?? 0
    group.total_fat_g += meal.total_fat_g ?? 0
    group.total_carb_g += meal.total_carb_g ?? 0
  }

  const ordered: GroupTotals[] = []
  for (const t of MEAL_TYPE_ORDER) {
    const g = map.get(t)
    if (g) ordered.push(g)
  }
  for (const [k, g] of map) {
    if (!isMealType(k)) {
      ordered.push(g)
    }
  }
  return ordered
}

function formatTime(value: string | null): string {
  if (!value) return ''
  return value.slice(0, 5)
}

type MealTableProps = {
  meals: MealLog[]
  /** 食品ごとの ⭐ 登録済み判定に使うお気に入り一覧 */
  templates: FoodTemplate[]
  onChanged: () => void
  onTemplateCreated: () => Promise<void>
}

export function MealTable({ meals, templates, onChanged, onTemplateCreated }: MealTableProps) {
  const groups = useMemo(() => groupMealsByType(meals), [meals])
  const favoriteNames = useMemo(() => singleItemTemplateNames(templates), [templates])

  // デフォルトで全グループ展開（collapsed が空集合なら全展開）
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set())
  const [editing, setEditing] = useState<MealLog | null>(null)
  // 登録〜一覧の再読み込みが終わるまでは連打で二重登録しないよう押せなくする
  const [favoritingIds, setFavoritingIds] = useState<Set<number>>(new Set())

  const handleFavorite = async (item: MealItem) => {
    setFavoritingIds((prev) => new Set(prev).add(item.id))
    try {
      await createTemplateFromMealItems({ item_ids: [item.id] })
      await onTemplateCreated()
      toast.success(`「${item.name}」をお気に入りに登録しました`)
    } catch {
      toast.error('お気に入り登録に失敗しました')
    } finally {
      setFavoritingIds((prev) => {
        const next = new Set(prev)
        next.delete(item.id)
        return next
      })
    }
  }

  const toggle = (meal_type: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev)
      if (next.has(meal_type)) {
        next.delete(meal_type)
      } else {
        next.add(meal_type)
      }
      return next
    })
  }

  if (groups.length === 0) {
    return <p className="py-2 text-xs text-muted-foreground">まだ記録がありません。画面下の入力欄から記録できます</p>
  }

  return (
    <>
      <div className="overflow-hidden rounded-md border border-border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-35 py-2">食事</TableHead>
              <TableHead className="py-2 text-right tabular-nums">kcal</TableHead>
              <TableHead className="py-2 text-right tabular-nums">P</TableHead>
              <TableHead className="py-2 text-right tabular-nums">F</TableHead>
              <TableHead className="py-2 text-right tabular-nums">C</TableHead>
              <TableHead className="w-10 py-2" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {groups.map((group) => {
              const knownType = isMealType(group.meal_type) ? group.meal_type : null
              const TypeIcon = knownType ? MEAL_TYPE_ICONS[knownType] : null
              const typeLabel = knownType ? MEAL_TYPE_LABELS[knownType] : group.meal_type
              const isOpen = !collapsed.has(group.meal_type)
              const isSnack = group.meal_type === 'snack'
              const singleMeal = group.meals.length === 1 ? group.meals[0] : undefined
              return (
                <Fragment key={group.meal_type}>
                  {/* グループ行 */}
                  <TableRow className="cursor-pointer" onClick={() => toggle(group.meal_type)}>
                    <TableCell className="py-2 font-medium">
                      <div className="flex items-center gap-1.5">
                        {isOpen ? <ChevronDown className="size-3.5" /> : <ChevronRight className="size-3.5" />}
                        {TypeIcon && <TypeIcon className="size-3.5" />}
                        <span>{typeLabel}</span>
                        {isSnack && group.meals.length > 1 && (
                          <span className="text-xs text-muted-foreground">({group.meals.length})</span>
                        )}
                        {singleMeal?.eaten_at && (
                          <span className="text-xs text-muted-foreground tabular-nums">
                            {formatTime(singleMeal.eaten_at)}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="py-2 text-right tabular-nums font-medium">
                      {Math.round(group.total_calories)}
                    </TableCell>
                    <TableCell className="py-2 text-right tabular-nums text-blue-400">
                      {group.total_protein_g.toFixed(0)}
                    </TableCell>
                    <TableCell className="py-2 text-right tabular-nums text-yellow-400">
                      {group.total_fat_g.toFixed(0)}
                    </TableCell>
                    <TableCell className="py-2 text-right tabular-nums text-green-400">
                      {group.total_carb_g.toFixed(0)}
                    </TableCell>
                    <TableCell className="py-2 text-right">
                      {singleMeal && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="size-7"
                          onClick={(e) => {
                            e.stopPropagation()
                            setEditing(singleMeal)
                          }}
                        >
                          <Pencil className="size-3.5" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>

                  {/* 展開時 */}
                  {isOpen &&
                    group.meals.map((meal) => (
                      <Fragment key={`meal-${meal.id}`}>
                        {/* snackで複数件のときに時刻＋編集ボタン行を挟む */}
                        {isSnack && group.meals.length > 1 && (
                          <TableRow className="bg-muted/30">
                            <TableCell colSpan={5} className="py-1.5 pl-8">
                              <span className="text-xs text-muted-foreground tabular-nums">
                                {meal.eaten_at ? formatTime(meal.eaten_at) : '(時刻未設定)'}
                              </span>
                            </TableCell>
                            <TableCell className="py-1.5 text-right">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="size-6"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  setEditing(meal)
                                }}
                              >
                                <Pencil className="size-3" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        )}
                        {meal.items.map((item) => {
                          const displayQuantity = scaleQuantity(item.quantity, item.scale ?? 1)
                          const isFavorite = favoriteNames.has(item.name.trim())
                          return (
                            <TableRow key={`item-${item.id}`} className="bg-muted/10">
                              <TableCell className="py-1 pl-8 text-xs">
                                <span className="truncate">{item.name}</span>
                                {displayQuantity && (
                                  <span className="ml-1 text-muted-foreground">({displayQuantity})</span>
                                )}
                              </TableCell>
                              <TableCell className="py-1 text-right tabular-nums text-xs text-muted-foreground">
                                {Math.round(item.calories)}
                              </TableCell>
                              <TableCell className="py-1 text-right tabular-nums text-xs text-muted-foreground">
                                {item.protein_g.toFixed(0)}
                              </TableCell>
                              <TableCell className="py-1 text-right tabular-nums text-xs text-muted-foreground">
                                {item.fat_g.toFixed(0)}
                              </TableCell>
                              <TableCell className="py-1 text-right tabular-nums text-xs text-muted-foreground">
                                {item.carb_g.toFixed(0)}
                              </TableCell>
                              <TableCell className="py-1 text-right">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="size-6"
                                  disabled={isFavorite || favoritingIds.has(item.id)}
                                  onClick={() => void handleFavorite(item)}
                                  title={isFavorite ? 'お気に入り登録済み' : 'お気に入りに登録'}
                                  aria-label={isFavorite ? 'お気に入り登録済み' : 'お気に入りに登録'}
                                >
                                  <Star className={`size-3 ${isFavorite ? 'fill-amber-400 text-amber-400' : ''}`} />
                                </Button>
                              </TableCell>
                            </TableRow>
                          )
                        })}
                      </Fragment>
                    ))}
                </Fragment>
              )
            })}
          </TableBody>
        </Table>
      </div>

      <MealSheet
        meal={editing}
        open={editing !== null}
        onOpenChange={(open) => {
          if (!open) setEditing(null)
        }}
        onSaved={onChanged}
      />
    </>
  )
}
